// react
import { FC, useState, useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom'
// hooks
import { useTranslation } from '../../../hooks'
// constants
import { MPC, EVM } from '@/components/shared/constants'
// components
import { X } from 'lucide-react'
import { Selector } from '@/components/shared/components/selector'
import { AccountSelector } from '@/components/shared/components/selector/Account'
import MpcDeposit from '@/components/Deposit/src/components/mpc'
import EvmDeposit from '@/components/Deposit/src/components/evm'
// shared
import { isMpcWalletType, isEvmWalletType } from '@/components/shared/utils'
// types
import { DepositProps } from '../types'
import { ChainInfo } from '@/types'

export const Deposit: FC<DepositProps> = ({
  isOpen,
  isLoading = false,
  onClose,
  currentActiveAccount,
  chains,
  spotChains = [],
  defaultChainId,
  defaultAccountType = 'perpetual',
  platformBalance = '0',
  wagmiHooks,
  onDeposit,
  onCopy,
  onChainChange,
  onTokenChange,
}) => {
  const { t } = useTranslation()

  // Account type state: 'perpetual' uses chains, 'spot' uses spotChains
  const [accountType, setAccountType] = useState<'perpetual' | 'spot'>(
    defaultAccountType
  )

  // Helper to get current data source by account type
  const getDataSource = useCallback(
    (type: 'perpetual' | 'spot') =>
      type === 'spot' && spotChains && spotChains.length > 0
        ? spotChains
        : chains,
    [chains, spotChains]
  )

  const initialChains = getDataSource(defaultAccountType)

  const [selectedChainId, setSelectedChainId] = useState(() => {
    if (!initialChains || initialChains.length === 0) return 1
    return defaultChainId || initialChains[0]?.chainId || 1
  })

  const dataSourceChains: ChainInfo[] = useMemo(
    () => getDataSource(accountType),
    [accountType, getDataSource]
  )

  const currentActiveChain =
    dataSourceChains.find(chain => chain.chainId === selectedChainId) ||
    dataSourceChains[0]

  const [selectedToken, setSelectedToken] = useState(() => {
    if (!initialChains || initialChains.length === 0) return ''

    const targetChainId = defaultChainId || initialChains[0]?.chainId
    const preferredChain =
      initialChains.find(c => c.chainId === targetChainId) || initialChains[0]

    // 若优选链没有代币，挑选第一个有代币的链
    const chainWithTokens =
      (preferredChain?.tokenList || []).length > 0
        ? preferredChain
        : initialChains.find(c => (c.tokenList || []).length > 0)

    const token = chainWithTokens?.tokenList?.[0]
    return token?.token || token?.symbol || ''
  })

  const handleChainChange = useCallback(
    (chainId: number) => {
      setSelectedChainId(chainId)
      onChainChange?.(chainId)
    },
    [onChainChange]
  )

  const handleTokenChange = useCallback(
    (tokenSymbol: string) => {
      setSelectedToken(tokenSymbol)
      onTokenChange?.(tokenSymbol)
    },
    [onTokenChange]
  )

  if (!isOpen) return null

  // Render at document.body via portal to ensure global overlay above all
  return ReactDOM.createPortal(
    <div
      className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen"
      style={{
        zIndex: 98,
      }}
    >
      {/* overlay + panel container */}
      <div
        className="fixed top-0 left-0 right-0 bottom-0 z-[99] flex items-center justify-center"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
        onClick={() => {
          onClose()
        }}
        aria-hidden="true"
      >
        {/* panel */}
        <div
          className="relative w-[420px] max-h-[85vh] bg-fill-page-secondary overflow-auto rounded-lg p-6 shadow-xl border-[2px] border-solid border-line-divider-secondary"
          onClick={e => {
            e.stopPropagation()
          }}
        >
          {/* header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              {t('deposit.title')}
            </h2>
            <button
              className="text-gray-400 hover:text-gray-200 transition-colors"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Account selector */}
          <AccountSelector
            selectedAccount={accountType}
            onAccountChange={account => {
              const nextType = (account as 'perpetual' | 'spot') || 'perpetual'
              setAccountType(nextType)

              const nextChains = getDataSource(nextType)
              if (!nextChains || nextChains.length === 0) return

              // Try to keep same chainId if exists; otherwise switch to the first
              const keepChain = nextChains.find(
                c => c.chainId === selectedChainId
              )
              const nextSelectedChainId = keepChain
                ? keepChain.chainId
                : nextChains[0].chainId
              setSelectedChainId(nextSelectedChainId)
              onChainChange?.(nextSelectedChainId)

              // Try to keep the same token if available; otherwise pick first token from new chain
              const nextChain = nextChains.find(
                c => c.chainId === nextSelectedChainId
              )
              const tokens = nextChain?.tokenList || []
              const exists = tokens.some(
                t => (t.symbol || t.token) === selectedToken
              )
              const nextToken = exists
                ? selectedToken
                : tokens[0]?.symbol || tokens[0]?.token || ''
              if (nextToken) {
                setSelectedToken(nextToken)
                onTokenChange?.(nextToken)
              }
            }}
            className="w-full"
          />

          <Selector
            currentActiveChain={currentActiveChain}
            chainList={dataSourceChains}
            onChainChange={handleChainChange}
            curSelectToken={selectedToken}
            onTokenChange={handleTokenChange}
            walletType={isMpcWalletType(currentActiveAccount) ? MPC : EVM}
          />

          {/* MPC view */}
          {isMpcWalletType(currentActiveAccount) && (
            <MpcDeposit
              address={currentActiveAccount.smartWalletAddress}
              currentActiveChain={currentActiveChain}
              selectedToken={selectedToken}
              onDeposit={onDeposit}
              onCopy={onCopy}
              onClose={onClose}
            />
          )}
          {/* EVM view */}
          {isEvmWalletType(currentActiveAccount) && (
            <EvmDeposit
              address={currentActiveAccount.ethAddress}
              currentActiveChain={currentActiveChain}
              selectedToken={selectedToken}
              accountType={accountType}
              platformBalance={platformBalance}
              isLoading={isLoading}
              wagmiHooks={wagmiHooks}
              onChainChange={handleChainChange}
              onDeposit={onDeposit}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
